// #336 S2: the type of each round (normal, deload, test) on the editor Draft.
// `Draft.roundTypes` is index-parallel to the rounds, like `inactiveWeeks`:
// every action that moves, inserts, removes or resizes rounds moves it too
// (programRoundEdits, programMatrixEdits, setCycleWeeks, addWeekCloning), or
// a type drifts off its round. Absent: every round is normal. Keep free of
// React and any UI import. Its imports are types only, so it adds no edge to
// the runtime import graph.
import type { Draft } from "./programEdit.js";
import type { RoundType } from "./schemas/programV3.js";

/** The words of the two types a label names; normal has none. */
export type RoundTypeWords = Record<Exclude<RoundType, "normal">, string>;

/** Round `w`'s type: normal where none is stored. */
export const roundTypeAt = (draft: { roundTypes?: readonly RoundType[] }, w: number): RoundType =>
  draft.roundTypes?.[w] ?? "normal";

/** Some round is not normal: the types say something. */
export const usesRoundTypes = (p: { roundTypes?: readonly string[] }): boolean =>
  (p.roundTypes ?? []).some((t) => t !== "normal");

/** `types` fit to `n` rounds: cut, or grown with `grown` rounds (normal). */
export const resizeRoundTypes = (
  types: readonly RoundType[],
  n: number,
  grown: RoundType = "normal",
): RoundType[] => Array.from({ length: n }, (_, i) => types[i] ?? grown);

/** The draft with its types rewritten by `f`, which gets one per round
 *  (fitted to cycleWeeks first, so a short array yields no undefined).
 *  Absent stays absent. */
export const mapRoundTypes = (draft: Draft, f: (types: RoundType[]) => RoundType[]): Draft =>
  draft.roundTypes
    ? { ...draft, roundTypes: f(resizeRoundTypes(draft.roundTypes, draft.cycleWeeks)) }
    : draft;

/** Round `w` given `type`. Normal on a draft with no types: the same draft. */
export const setRoundType = (draft: Draft, w: number, type: RoundType): Draft => {
  if (w < 0 || w >= draft.cycleWeeks || roundTypeAt(draft, w) === type) return draft;
  const types = resizeRoundTypes(draft.roundTypes ?? [], draft.cycleWeeks);
  types[w] = type;
  return { ...draft, roundTypes: types };
};

/** What the save sends: one type per round, or nothing when all are normal. */
export const roundTypesForSave = (draft: Draft): RoundType[] | undefined =>
  usesRoundTypes(draft) ? resizeRoundTypes(draft.roundTypes ?? [], draft.cycleWeeks) : undefined;

/** The round's label, «W4», one notation for the editor's heads, chips and
 *  menus. With a deload or test round and the UI's words: «W4 · Deload». */
export const programRoundLabel = (n: number, type?: RoundType, words?: RoundTypeWords): string =>
  type && type !== "normal" && words ? `W${n} · ${words[type]}` : `W${n}`;
